import { createI18n } from 'vue-i18n'
import _accounting from 'accounting'
import { ACCOUNTING_SETTINGS, LANGUAGES } from './lib/consts.ts'
import { getCurrentLocale, LOCALE_KEY } from '@/lib/locale'
import {
  pluralizationEN,
  pluralizationES,
  pluralizationPL,
  pluralizationPT,
  pluralizationUK,
} from '@/lib/i18n-plural-rules'
import { createLegacyTc } from '@/lib/i18n-legacy-tc'

import {
  country,
  language,
  baseUrl,
  basePath,
} from '@/router/location-params.ts'

// fallback (en) language messages
import fallbackMessages from '@/messages/en.json'

import { getMomentFormats } from '@/lib/moment-formats.ts'
import moment from 'moment'

// moment and accounting formats for current country & language
export const MOMENT_FORMATS = {} as ReturnType<typeof getMomentFormats>
export const accounting = _accounting

type LocalizeResult = {
  country: string;
  language: string;
  locale: string;
  i18n: ReturnType<typeof createI18n>;
  baseUrl: string;
  basePath: string;
};

/*
 *
 * setup & modules/components/... related to country & language
 *
 */
export async function localize (): Promise<LocalizeResult> {
  return new Promise((resolve, reject) => {
    /*
     * we need to delay i18n initialization, till VueI18n plugin is
     * registered; see: `@/main.js` - method `localize` is called
     * after `registerPlugins`
     */
    const i18n = createI18n({
      legacy: true,
      locale: 'en',
      fallbackLocale: 'en',
      messages: {
        en: sanitizeMessages(fallbackMessages),
      },
      pluralizationRules: {
        pl: pluralizationPL,
        en: pluralizationEN,
        'en-gb': pluralizationEN,
        pt: pluralizationPT,
        es: pluralizationES,
        'es-es': pluralizationES,
        uk: pluralizationUK,
        de: pluralizationEN,
      },
    })

    const originalT = i18n.global.t

    // vue-i18n v11 removed the Legacy API `tc` / `$tc`; restore the v10 behaviour
    // on top of the original `t` (see src/lib/i18n-legacy-tc.ts)
    const legacyTc = createLegacyTc(originalT)
    Object.assign(i18n.global, { tc: legacyTc })

    const installI18n = i18n.install
    i18n.install = (app, ...options) => {
      installI18n(app, ...options)
      app.config.globalProperties.$tc = legacyTc
    }

    // HACK-VUE3 - we get error "Invalid arguments" when we call `i18n.global.t` with null or undefined
    // there may be many such places in the project at the moment....
    i18n.global.t = (key) => {
      if (key) return originalT.call(i18n, key)
      return ''
    }

    // if unsupported languaged - redirect to en language
    const supportedLanguages = LANGUAGES.map(l => l.code)

    const locale = getCurrentLocale(language, country)

    if (!supportedLanguages.includes(locale)) {
      const a = `/${language}-${country}/`
      const b = `/en-${country}/`

      localStorage.removeItem(LOCALE_KEY)

      const url = new URL(window.location.href.replace(a, b))
      url.searchParams.delete('locale')
      window.location.href = url.toString()
      return reject(new Error('Unsupported language'))
    }

    localeMoment(locale)

    // load translation messages for current language & country
    loadMessages(i18n, language, locale).then(() => {
      // if moment is set by language, format should be too
      Object.assign(MOMENT_FORMATS, getMomentFormats(country, i18n))
      accounting.settings = ACCOUNTING_SETTINGS[country]
      resolve({ country, language, locale, i18n, baseUrl, basePath })
    })
  })
}

function loadMessages (i18n, language, locale) {
  // shortcut: language is same as default
  if (i18n.global.locale === locale) {
    return Promise.resolve(setI18nLanguage(i18n, locale, language))
  }

  // workaround for error, caused by: `@/messages/${lang}.js`
  return import(`./messages/${locale}.json`).then(messages => {
    i18n.global.setLocaleMessage(locale, sanitizeMessages(messages.default))
    return setI18nLanguage(i18n, locale, language)
  })
}

// HACK-VUE3
function sanitizeMessages (messages) {
  const sanitized = {}
  for (const [key, value] of Object.entries(messages)) {
    if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeMessages(value)
    } else if (typeof value === 'string') {
      sanitized[key] = value.replace(/@/g, match => `{'${match}'}`)
    } else {
      sanitized[key] = value
    }
  }
  return sanitized
}

function setI18nLanguage (i18n, locale, language) {
  i18n.global.locale = locale
  document.querySelector('html').setAttribute('lang', language)
}

// we use our own translations for CKEditor to have them optimal and more configurable

const MOMENT_LOCALES: Record<string, () => Promise<unknown>> = {
  en: () => import('moment/dist/locale/en-gb'),
  'en-gb': () => import('moment/dist/locale/en-gb'),
  pl: () => import('moment/dist/locale/pl'),
  es: () => import('moment/dist/locale/es'),
  'es-es': () => import('moment/dist/locale/es'),
  'es-mx': () => import('moment/dist/locale/es-mx'),
  pt: () => import('moment/dist/locale/pt'),
  'pt-br': () => import('moment/dist/locale/pt-br'),
  fr: () => import('moment/dist/locale/fr'),
  vi: () => import('moment/dist/locale/vi'),
  uk: () => import('moment/dist/locale/uk'),
  zh: () => import('moment/dist/locale/zh-cn'),
  'zh-cn': () => import('moment/dist/locale/zh-cn'),
  de: () => import('moment/dist/locale/de'),
}

async function localeMoment (locale) {
  try {
    await MOMENT_LOCALES[locale]()
    moment.locale(locale)
  } catch (e) {
    console.error(`moment-locale: failed to load ${locale}, back to en`)
  }
}
